/**
 * What this person would like to be called.
 *
 * It appears twice, and both places are deliberate — the same two places, and
 * for the same reasons, as the language picker beside it. On the way in, because
 * the name is what every other person in the kennel will see against a weight
 * somebody has to trust; and on the people screen, because that is where anyone
 * looking for "my account" looks, and because a name typed in a hurry at eleven
 * at night has to be correctable afterwards.
 *
 * Optional, genuinely: empty is a real answer, and `displayNameOf` in platform
 * turns it into the front of the email address wherever a name is drawn. Which
 * is why the placeholder is that fallback and the hint says so out loud — the
 * field must not look like something the app is waiting for.
 *
 * Saved on blur, not on every keystroke: a Firestore write per character is a
 * write per character, and the sentence is only meaningful once it is finished.
 * Not awaited either — see api/mutations.ts. There is no "saved" tick, because
 * the thing that proves it saved is the name in the header changing under the
 * same thumb, and it does so on the next frame.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthProvider.tsx';
import { setProfileDisplayName } from '@/api/mutations.ts';
import { TextField } from '@/components/ui.tsx';
import { useProfile } from '@/tenant/TenantProvider.tsx';

export const DisplayNameField = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { chosen, fallback } = useProfile();

  /**
   * The draft is stored WITH the value it started from, and what the input shows
   * is derived from the two rather than reset by an effect when the document
   * arrives. The listener echoes this person's own write back within a frame of
   * the blur that made it — a `useEffect` reset on that echo is a render in which
   * the field flickers back to what it used to say.
   */
  const [draft, setDraft] = useState<{ seed: string; value: string } | null>(null);
  const value = draft !== null && draft.seed === chosen ? draft.value : chosen;

  const save = (): void => {
    if (user === null || value.trim() === chosen) return;
    setProfileDisplayName(user.uid, value);
  };

  return (
    <TextField
      className={className}
      label={`${t('settings.displayName')} · ${t('common.optional')}`}
      placeholder={fallback}
      value={value}
      autoComplete="nickname"
      // `words`, not `sentences`: this is a name, and iOS otherwise offers
      // "Anna kowalska" to anyone who types both halves of theirs.
      autoCapitalize="words"
      spellCheck={false}
      onChange={(event) => setDraft({ seed: chosen, value: event.target.value })}
      onBlur={save}
      // Two hints, one line: while the field is empty the useful thing to say is
      // what will happen if it stays that way, and once there is a name in it
      // the useful thing to say is who will read it.
      hint={
        value.trim() === '' && fallback !== ''
          ? t('settings.displayNameFallback', { name: fallback })
          : t('settings.displayNameHint')
      }
    />
  );
};
